import type {
  InvoiceCandidatesApiData,
  InvoiceCandidatesApiResponses,
  InvoiceCreateApiData,
  InvoiceCreateApiResponses,
  InvoiceCreateFromOrdersApiData,
  InvoiceCreateFromOrdersApiResponses,
  InvoiceDeleteApiData,
  InvoiceDeleteApiResponses,
  InvoiceGetApiData,
  InvoiceGetApiResponses,
  InvoiceListApiData,
  InvoiceListApiResponses,
  InvoicePatchApiData,
  InvoicePatchApiResponses,
  InvoiceToExcelApiData,
  InvoiceToExcelApiResponses,
  InvoiceToPdfApiData,
  InvoiceToPdfApiResponses
} from '@navio/nsdk';

import { downloadBlobInBrowser, getFilenameFromContentDisposition } from '../../browserBlobDownload';
import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getInvoiceUrls = {
  getPaginatedInvoices: dataEndpoint<InvoiceListApiData, InvoiceListApiResponses>({
    getUrl: (queryParams) => apiUrl('invoices/', queryParams)
  }),
  getInvoice: dataEndpoint<InvoiceGetApiData, InvoiceGetApiResponses>({
    getUrl: ({ invoiceId }) => apiUrl(`invoices/${invoiceId}/`)
  }),
  deleteInvoice: dataEndpoint<InvoiceDeleteApiData, InvoiceDeleteApiResponses>({
    getUrl: ({ invoiceId }) => apiUrl(`invoices/${invoiceId}/`)
  }),
  postInvoiceToExcel: dataEndpoint<InvoiceToExcelApiData, InvoiceToExcelApiResponses>({
    getUrl: ({ invoiceId }) => apiUrl(`invoices/${invoiceId}/to-excel/`),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob'
  }),
  postInvoice: responseEndpoint<InvoiceCreateApiData, InvoiceCreateApiResponses>({
    getUrl: () => apiUrl('invoices/'),
    getBody: (data) => data
  }),
  // POST because the input is a filter object rather than an identifier, but it
  // writes nothing -- consume it with usePostAsGetQuery so the resolved period
  // is cached and refetches when the scope changes.
  postInvoiceCandidates: responseEndpoint<InvoiceCandidatesApiData, InvoiceCandidatesApiResponses>({
    getUrl: () => apiUrl('invoices/candidates/'),
    getBody: (data) => data
  }),
  postInvoiceFromOrders: responseEndpoint<InvoiceCreateFromOrdersApiData, InvoiceCreateFromOrdersApiResponses>({
    getUrl: () => apiUrl('invoices/from-orders/'),
    getBody: (data) => data
  }),
  patchInvoice: responseEndpoint<InvoicePatchApiData, InvoicePatchApiResponses>({
    getUrl: ({ invoiceId }) => apiUrl(`invoices/${invoiceId}/`),
    getBody: (data) => data
  }),
  // Removed in #5077 because the renderer resolved through the singular
  // `Invoice.order`, null on every invoice in production, so it raised on
  // every click. It now renders from order membership, which is what that
  // commit said it was waiting for.
  postInvoicePdf: dataEndpoint<InvoiceToPdfApiData, InvoiceToPdfApiResponses>({
    getUrl: ({ invoiceId }) => apiUrl(`invoices/${invoiceId}/to-pdf/`),
    responseType: 'blob',
    handleRes: async (res) => {
      const blob = res.data;
      const filename = getFilenameFromContentDisposition(res.headers['content-disposition'], 'invoice.pdf');
      downloadBlobInBrowser(blob, filename);
      return blob;
    }
  })
};
